import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, USER_ROLES } from '../context/AuthContext';
import './Layout.css';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({
    dashboard: true,
    inventory: false,
    sales: false,
    production: false,
    management: false,
    reports: false
  });
  const { logout, user, hasPermission } = useAuth();
  const location = useLocation();

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getNavigationItems = () => {
    const categories = {
      dashboard: {
        name: 'Dashboard',
        icon: '📊',
        items: [
          { path: '/dashboard', name: 'Ana Sayfa', icon: '🏠', permission: 'dashboard' }
        ]
      },
      inventory: {
        name: 'Stok & Depo',
        icon: '📦',
        items: [
          { path: '/stock-card', name: 'Stok Kartı', icon: '🏷️', permission: 'stock_card' },
          { path: '/stock', name: 'Stok Yönetimi', icon: '📦', permission: 'stock' },
          { path: '/stock-movement', name: 'Stok Hareketi', icon: '🔄', permission: 'stock_movement' },
          { path: '/warehouse', name: 'Depo Yönetimi', icon: '🏭', permission: 'warehouse' },
          { path: '/shelf', name: 'Raf Yönetimi', icon: '📋', permission: 'shelf' },
          { path: '/warehouse-terminal', name: 'Depo Terminali', icon: '📱', permission: 'warehouse_terminal' }
        ]
      },
      sales: {
        name: 'Satış & Fatura',
        icon: '💰',
        items: [
          { path: '/orders', name: 'Sipariş Yönetimi', icon: '📋', permission: 'orders' },
          { path: '/invoices', name: 'Fatura Yönetimi', icon: '🧾', permission: 'invoices' },
          { path: '/order-to-invoice', name: 'Sipariş→Fatura', icon: '➡️', permission: 'invoices' },
          { path: '/b2b', name: 'B2B Yönetimi', icon: '🏢', permission: 'b2b' },
          { path: '/b2c', name: 'B2C Yönetimi', icon: '🛒', permission: 'b2c' },
          { path: '/customer-relations', name: 'Müşteri İlişkileri', icon: '📞', permission: 'customer_relations' }
        ]
      },
      production: {
        name: 'Üretim & İş Emri',
        icon: '⚙️',
        items: [
          { path: '/work-orders', name: 'İş Emirleri', icon: '🔧', permission: 'work_orders' },
          { path: '/work-order-to-invoice', name: 'İş Emri→Fatura', icon: '⚙️', permission: 'work_orders' },
          { path: '/production', name: 'Üretim Yönetimi', icon: '⚙️', permission: 'production' },
          { path: '/production-recipe', name: 'Üretim Reçetesi', icon: '📝', permission: 'production' },
          { path: '/production-tracking', name: 'Üretim Takibi', icon: '📈', permission: 'production' }
        ]
      },
      management: {
        name: 'Yönetim',
        icon: '👥',
        items: [
          { path: '/users', name: 'Kullanıcı Yönetimi', icon: '👥', permission: 'users' },
          { path: '/user-roles', name: 'Kullanıcı Rolleri', icon: '🔐', permission: 'user_roles' },
          { path: '/hr', name: 'İK Yönetimi', icon: '👥', permission: 'hr' },
          { path: '/consultant', name: 'Danışman Portalı', icon: '👨‍💼', permission: 'consultant' }
        ]
      },
      reports: {
        name: 'Raporlar & Ayarlar',
        icon: '📊',
        items: [
          { path: '/reports', name: 'Raporlar', icon: '📊', permission: 'reports' },
          { path: '/settings', name: 'Sistem Ayarları', icon: '⚙️', permission: 'settings' }
        ]
      }
    };

    return categories;
  };

  const handleLogout = () => {
    logout();
  };

  const navigation = getNavigationItems();

  const renderNavItem = (item) => {
    if (!hasPermission(item.permission)) return null;
    
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
        onClick={() => setSidebarOpen(false)}
      >
        <span className="nav-icon">{item.icon}</span>
        <span className="nav-text">{item.name}</span>
      </Link>
    );
  };

  const renderCategory = (categoryKey, category) => {
    const hasVisibleItems = category.items.some(item => hasPermission(item.permission));
    if (!hasVisibleItems) return null;

    const isExpanded = expandedCategories[categoryKey];
    const hasActiveItem = category.items.some(item => 
      hasPermission(item.permission) && location.pathname === item.path
    );

    return (
      <div key={categoryKey} className="nav-category">
        <button
          className={`category-header ${hasActiveItem ? 'active' : ''}`}
          onClick={() => toggleCategory(categoryKey)}
        >
          <span className="category-icon">{category.icon}</span>
          <span className="category-name">{category.name}</span>
          <span className={`category-arrow ${isExpanded ? 'expanded' : ''}`}>
            ▼
          </span>
        </button>
        <div className={`category-items ${isExpanded ? 'expanded' : ''}`}>
          {category.items.map(renderNavItem)}
        </div>
      </div>
    );
  };

  return (
    <div className="layout">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <span className="logo-icon">🏢</span>
            <h2>ASTOS ERP</h2>
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? '✕' : '☰'}
          </button>
        </div>

        <div className="user-info">
          <div className="user-avatar">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div className="user-details">
            <div className="user-name">{user?.name || 'Kullanıcı'}</div>
            <div className="user-role">{getRoleDisplayName(user?.role)}</div>
          </div>
        </div>

        <nav className="sidebar-nav">
          {Object.entries(navigation).map(([key, category]) => 
            renderCategory(key, category)
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="footer-info">
            <div className="company-info">
              <span className="company-name">ASTOS ERP</span>
              <span className="version">v2.1.0</span>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <span className="nav-icon">🚪</span>
            <span className="nav-text">Çıkış Yap</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <header className="header">
          <div className="header-left">
            <button 
              className="menu-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              ☰
            </button>
            <div className="breadcrumb">
              <span className="breadcrumb-item">Firma Yönetimi</span>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-item">
                {Object.values(navigation).flatMap(cat => cat.items).find(item => 
                  item.path === location.pathname
                )?.name || 'Ana Sayfa'}
              </span>
            </div>
          </div>
          <div className="header-right">
            <div className="header-actions">
              <button className="notification-btn">
                <span className="notification-icon">🔔</span>
                <span className="notification-badge">3</span>
              </button>
              <div className="user-menu">
                <div className="user-info-text">
                  <span className="user-name">{user?.name}</span>
                  <span className="user-role">{getRoleDisplayName(user?.role)}</span>
                </div>
                <div className="user-avatar-small">
                  {user?.name?.charAt(0) || 'U'}
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="content">
          {children}
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

const getRoleDisplayName = (role) => {
  const roleNames = {
    [USER_ROLES.ADMIN]: 'Sistem Yöneticisi',
    [USER_ROLES.MANAGER]: 'Yönetici',
    [USER_ROLES.SALES]: 'Satış',
    [USER_ROLES.WAREHOUSE]: 'Depo',
    [USER_ROLES.CONSULTANT]: 'Danışman',
    [USER_ROLES.HR]: 'İK',
    [USER_ROLES.CUSTOMER_SERVICE]: 'Müşteri Hizmetleri',
    [USER_ROLES.PRODUCTION]: 'Üretim',
    [USER_ROLES.B2B_USER]: 'B2B Kullanıcısı',
    [USER_ROLES.B2C_USER]: 'B2C Kullanıcısı'
  };
  return roleNames[role] || 'Kullanıcı';
};

export default Layout; 